import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { Button } from './Button';
import { VStack } from './Stack';
import { Text } from './Text';

interface EmptyStateAction {
  label: string;
  onPress: () => void;
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  dashed?: boolean;
}

const ICON_CONTAINER_SIZE = 80;

export function EmptyState({
  icon,
  title,
  description,
  action,
  dashed = false,
}: Readonly<EmptyStateProps>) {
  return (
    <View style={[styles.container, dashed && styles.dashedBorder]}>
      <VStack spacing="lg" align="center">
        {icon ? <View style={styles.iconContainer}>{icon}</View> : null}
        <Text variant="h6" color={colors.text.primary} align="center">
          {title}
        </Text>
        {description ? (
          <Text
            variant="bodySmall"
            color={colors.text.tertiary}
            align="center"
            style={styles.description}
          >
            {description}
          </Text>
        ) : null}
        {action ? (
          <Button variant="primary" onPress={action.onPress}>
            {action.label}
          </Button>
        ) : null}
      </VStack>
    </View>
  );
}

EmptyState.displayName = 'EmptyState';

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing['2xl'],
  },
  dashedBorder: {
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: colors.border.muted,
    borderRadius: tokens.radius.lg,
  },
  iconContainer: {
    width: ICON_CONTAINER_SIZE,
    height: ICON_CONTAINER_SIZE,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  description: {
    maxWidth: 280,
  },
});
